import { c as _c } from "react/compiler-runtime";
import { Facet, FacetValue, SelectedFacet, SelectedFacetValue } from '../interfaces';
import React, { ReactElement, useState, useTransition } from 'react';
import { deriveFilterElement, formatSliderValue, parseToggleValue } from '../filterActionTracking';
import getPageType, { PageType } from '@spa/lib/tracking/getPageType';
import trackFiltersActions, { FiltersEventType, generateFacetData } from '../../../lib/tracking/trackFiltersActions';
import { usePathname, useRouter } from 'next/navigation';
import CategoryNavigationComponent from '@spa/components/facets/category-navigation/category-navigation.component';
import FacetComponent from '../facet/facet.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { PageTemplate } from '@spa/lib/cms/const';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import getAuthSessionUserId from '../../../lib/auth/getAuthSessionUserId';
import { scheduleTask } from '@spa/lib/utils/scheduleTask';
import selectLastQueryMetadata from '../../../lib/store/search/selectors/selectLastQueryMetadata';
import selectPageTemplate from '@spa/lib/store/router/selectors/selectPageTemplate';
import styles from './facets.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import updateSelectedFacetsQuery from '../../selected-facets/updateSelectedFacetsQuery';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useNextAuthOrSession from '../../../lib/react/hooks/useNextAuthOrSession';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
import { useSelector } from 'react-redux';
export interface Props {
  readonly facets: ReadonlyArray<Facet>;
  readonly selectedFacets: ReadonlyArray<SelectedFacet>;
}
const MIN_FACET_COUNT = 2;
function FacetsComponent(t0) {
  const $ = _c(43);
  const {
    facets,
    selectedFacets
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const pageTemplate = useSelector(selectPageTemplate);
  let t1;
  if ($[0] !== pageTemplate || $[1] !== pathname) {
    t1 = getPageType({
      pathname,
      pageTemplate
    });
    $[0] = pageTemplate;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pageType = t1;
  const searchParams = useRuntimeSearchParams();
  const {
    formatMessage
  } = useNamespacedIntl("facet");
  const {
    authSession,
    session
  } = useNextAuthOrSession();
  const lastQueryMetadata = useSelector(selectLastQueryMetadata);
  const [facetState, setFacetState] = useState(null);
  const [, startTransition] = useTransition();
  const userSelectedFacets = facetState !== null && facetState.basedOn === selectedFacets ? facetState.facets : selectedFacets;
  const anonymousUserId = session.guid;
  let t2;
  if ($[3] !== authSession) {
    t2 = getAuthSessionUserId(authSession);
    $[3] = authSession;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const userId = t2;
  let t3;
  if ($[5] !== anonymousUserId || $[6] !== lastQueryMetadata || $[7] !== pathname || $[8] !== router || $[9] !== searchParams || $[10] !== selectedFacets || $[11] !== startTransition || $[12] !== userId) {
    t3 = newSelectedFacets => {
      setFacetState({
        facets: newSelectedFacets,
        basedOn: selectedFacets
      });
      startTransition(() => {
        updateSelectedFacetsQuery(newSelectedFacets, router, pathname, searchParams);
      });
      scheduleTask(() => {
        trackFiltersActions(FiltersEventType.SELECTED, generateFacetData(newSelectedFacets), anonymousUserId, userId, lastQueryMetadata);
      });
    };
    $[5] = anonymousUserId;
    $[6] = lastQueryMetadata;
    $[7] = pathname;
    $[8] = router;
    $[9] = searchParams;
    $[10] = selectedFacets;
    $[11] = startTransition;
    $[12] = userId;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleFacetValueChange = t3;
  let t4;
  if ($[14] !== userSelectedFacets) {
    t4 = function addFacet(facetValueCode, facet, newLabel) {
      return [...userSelectedFacets, {
        code: facet.facetCode,
        type: facet.type === "values" ? "values" : "boolean",
        values: [{
          code: facetValueCode,
          label: newLabel
        }]
      }];
    };
    $[14] = userSelectedFacets;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const addFacet = t4;
  let t5;
  if ($[16] !== userSelectedFacets) {
    t5 = function toggleFacetValue(facetValueCode_0, facet_0, newLabel_0) {
      return userSelectedFacets.map(selectedFacet => {
        let newSelectedFacet = selectedFacet;
        const isMatchingFacet = selectedFacet.code === facet_0.facetCode && selectedFacet.type !== "range";
        if (isMatchingFacet) {
          const hasValue = selectedFacet.values.some(value => value.code === facetValueCode_0);
          const newValues = hasValue ? selectedFacet.values.filter(value_0 => value_0.code !== facetValueCode_0) : [...selectedFacet.values, {
            code: facetValueCode_0,
            label: newLabel_0
          }];
          newSelectedFacet = {
            ...selectedFacet,
            values: newValues
          };
        }
        return newSelectedFacet;
      });
    };
    $[16] = userSelectedFacets;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const toggleFacetValue = t5;
  let t6;
  if ($[18] !== addFacet || $[19] !== formatMessage || $[20] !== handleFacetValueChange || $[21] !== pageType || $[22] !== toggleFacetValue || $[23] !== userSelectedFacets) {
    t6 = function onFacetValueChange(facetValueCode_1, facet_1) {
      if (facet_1.type !== "range") {
        const newLabel_1 = facet_1.values.find(value_1 => value_1.code === facetValueCode_1)?.value ?? "";
        const isNewFacet = userSelectedFacets.find(selectedFacet_0 => selectedFacet_0.code === facet_1.facetCode) === undefined;
        const wasSelected = userSelectedFacets.some(selectedFacet_1 => selectedFacet_1.code === facet_1.facetCode && selectedFacet_1.type !== "range" && selectedFacet_1.values.some(value_2 => value_2.code === facetValueCode_1));
        const newSelectedFacets_0 = isNewFacet ? addFacet(facetValueCode_1, facet_1, newLabel_1) : toggleFacetValue(facetValueCode_1, facet_1, newLabel_1);
        handleFacetValueChange(newSelectedFacets_0);
        const isToggle = facet_1.type === "boolean";
        tracker.filterAction({
          filter_category: formatMessage(`facetNames.${facet_1.facetCode}`),
          filter_element: deriveFilterElement(facet_1),
          filter_interaction: wasSelected ? "remove" : "apply",
          filter_value: isToggle ? wasSelected ? false : parseToggleValue(newLabel_1) : newLabel_1,
          page_type: pageType
        });
      }
    };
    $[18] = addFacet;
    $[19] = formatMessage;
    $[20] = handleFacetValueChange;
    $[21] = pageType;
    $[22] = toggleFacetValue;
    $[23] = userSelectedFacets;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const onFacetValueChange = t6;
  let t7;
  if ($[25] !== formatMessage || $[26] !== handleFacetValueChange || $[27] !== pageType || $[28] !== userSelectedFacets) {
    t7 = function onFacetRangeChange(facetCode, min, max) {
      let newSelectedFacets_1;
      if (!userSelectedFacets.find(selectedFacet_3 => selectedFacet_3.code === facetCode)) {
        newSelectedFacets_1 = [...userSelectedFacets, {
          code: facetCode,
          type: "range",
          from: min,
          to: max
        }];
      } else {
        newSelectedFacets_1 = userSelectedFacets.map(selectedFacet_2 => {
          if (selectedFacet_2.code === facetCode && selectedFacet_2.type === "range") {
            return {
              ...selectedFacet_2,
              from: min,
              to: max
            };
          } else {
            return selectedFacet_2;
          }
        });
      }
      handleFacetValueChange(newSelectedFacets_1);
      tracker.filterAction({
        filter_category: formatMessage(`facetNames.${facetCode}`),
        filter_element: "slider",
        filter_interaction: "apply",
        filter_value: formatSliderValue(min, max),
        page_type: pageType
      });
    };
    $[25] = formatMessage;
    $[26] = handleFacetValueChange;
    $[27] = pageType;
    $[28] = userSelectedFacets;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const onFacetRangeChange = t7;
  let t8;
  if ($[30] !== facets || $[31] !== onFacetRangeChange || $[32] !== onFacetValueChange || $[33] !== pageType || $[34] !== userSelectedFacets) {
    let t9;
    if ($[36] !== onFacetRangeChange || $[37] !== onFacetValueChange || $[38] !== pageType || $[39] !== userSelectedFacets) {
      t9 = (facet_2, index) => facet_2.type === "values" && facet_2.facetCode === "categories.path" ? <CategoryNavigationComponent key={facet_2.facetCode} facet={facet_2} pageType={pageType} /> : (facet_2.type === "range" || facet_2.values.length >= MIN_FACET_COUNT) && <FacetComponent key={facet_2.facetCode} facet={facet_2} selectedFacet={userSelectedFacets.find(sf => sf.code === facet_2.facetCode)} onFacetRangeChange={onFacetRangeChange} onFacetValueChange={onFacetValueChange} isClosed={index > 4} pageType={pageType} />;
      $[36] = onFacetRangeChange;
      $[37] = onFacetValueChange;
      $[38] = pageType;
      $[39] = userSelectedFacets;
      $[40] = t9;
    } else {
      t9 = $[40];
    }
    t8 = facets.map(t9);
    $[30] = facets;
    $[31] = onFacetRangeChange;
    $[32] = onFacetValueChange;
    $[33] = pageType;
    $[34] = userSelectedFacets;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[41] !== t8) {
    t9 = <div className={styles.facets}><div className={styles.facets__container}><div className={styles["facets__facet-selectors"]}>{t8}</div></div></div>;
    $[41] = t8;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  return t9;
}
export default FacetsComponent;